import { lazy, Suspense, useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthGuard, GuestGuard } from "../features/auth/context/AuthGuard";
import LoginPage from "../features/auth/pages/LoginPage";
import WorkspaceSelectPage from "../features/auth/pages/WorkspaceSelectPage";
import WelcomeWizard from "../features/auth/pages/WelcomeWizard";
import AcceptInvitePage from "../features/auth/pages/AcceptInvitePage";
import AccountPendingPage from "../features/auth/pages/AccountPendingPage";
import AccountBlockedPage from "../features/auth/pages/AccountBlockedPage";
import VaultLayout from "./VaultLayout";
import HomePage from "./pages/HomePage";
import DrivePage from "./pages/DrivePage";
import CollectionsPage from "./pages/CollectionsPage";
import CollectionDetailPage from "./pages/CollectionDetailPage";
import ApprovalsPage from "./pages/ApprovalsPage";
import InsightsPage from "./pages/InsightsPage";
import LinksPage from "./pages/LinksPage";
import BulkRenderPage from "./pages/BulkRenderPage";
import RenderSettingsPage from "./pages/RenderSettingsPage";
import ModelViewPage from "./pages/ModelViewPage";
import { ErrorBoundary } from "@/shared/ui";
import StudioPage from "./pages/StudioPage";
import RingBuilderPage from "./pages/RingBuilderPage";
import SettingsPage from "./pages/SettingsPage";
import BrandingPage from "./pages/BrandingPage";
import StoragePage from "./pages/StoragePage";
import TeamPage from "./pages/TeamPage";
import BillingOverview from "./pages/BillingOverview";
import ChoosePlanPage from "./pages/ChoosePlanPage";
import EmbedPage from "./pages/EmbedPage";
import ShowcasePage from "./pages/ShowcasePage";
import ExplorePage from "./pages/ExplorePage";
import ContactPage from "./pages/ContactPage";
import ShowcaseModelPage from "./pages/ShowcaseModelPage";
import AdminLayout from "./AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminUserDetail from "./pages/admin/AdminUserDetail";
import AdminWorkspaces from "./pages/admin/AdminWorkspaces";
import AdminPlans from "./pages/admin/AdminPlans";
import AdminPresets from "./pages/admin/AdminPresets";
import AdminBilling from "./pages/admin/AdminBilling";
import AdminInvoices from "./pages/admin/AdminInvoices";
import AdminBroadcasts from "./pages/admin/AdminBroadcasts";
import AdminAudit from "./pages/admin/AdminAudit";
import AdminSupport from "./pages/admin/AdminSupport";

/** HDR Studio (/dev/hdr, /hdr): its own chunk — and, unlike DevConfiguratorPage below, it ships in
 *  production (the user wants vault.jewelitt.com/dev/hdr live).
 *
 *  No `lazy` + `<Suspense>` here (L7, lane L5's M1/M2, measured on a production build): React holds a
 *  Suspense fallback for at least ~300 ms after its chunk has arrived (its fallback throttle; measured
 *  300.7–321.7 ms), and that fallback was a LIGHT "Opening HDR Studio…" page flashed before a dark
 *  editor whose reference paints its welcome at 232 ms. The chunk starts loading at module evaluation
 *  when the app boots on the route (index.html also preloads it), the route renders nothing until it is
 *  in, and `index.html` keeps those frames black (`html.hdr-boot`). A failed chunk load is thrown to the
 *  route's ErrorBoundary. */
const HDR_ROUTE = /^\/(dev\/)?hdr\/?$/;
type HdrPageModule = typeof import("./pages/HdrStudioPage");
let hdrChunk: Promise<HdrPageModule> | null =
  typeof location !== "undefined" && HDR_ROUTE.test(location.pathname) ? import("./pages/HdrStudioPage") : null;
let HdrResolved: HdrPageModule["default"] | null = null;
function HdrStudioPage() {
  const [Page, setPage] = useState(() => HdrResolved);
  const [failed, setFailed] = useState<unknown>(null);
  useEffect(() => {
    if (Page) return;
    let alive = true;
    (hdrChunk ??= import("./pages/HdrStudioPage")).then(
      (m) => {
        HdrResolved = m.default;
        if (alive) setPage(() => m.default);
      },
      (e) => {
        hdrChunk = null;
        if (alive) setFailed(e ?? new Error("HDR Studio could not load"));
      },
    );
    return () => {
      alive = false;
    };
  }, [Page]);
  // The black boot background belongs to the HDR routes only.
  useEffect(() => () => document.documentElement.classList.remove("hdr-boot"), []);
  if (failed) throw failed;
  return Page ? <Page /> : null;
}
/** The public lookbook (/l/:token) registers Google's <model-viewer> (Lit) for its AR card.
 *  Imported eagerly it loaded Lit — and its "dev mode" console warning — on EVERY Vault
 *  page, HDR Studio included (round 3, C2-17); as its own chunk only the lookbook pays. */
const ClientLookbookPage = lazy(() => import("./pages/ClientLookbookPage"));

/** The shared link's configurator over a sample model, with no API (see the page).
 *  Dev builds only: the branch is dead code in production, so the chunk is never emitted. */
const DevConfiguratorPage = import.meta.env.DEV ? lazy(() => import("./pages/DevConfiguratorPage")) : null;

/**
 * `<Navigate to="/x" replace />` DROPS the query string. Share links look like
 * `/engagement?design=<token>` (designStore.shareUrlFor writes `design` onto the
 * current URL), so every legacy-builder redirect has to carry the search across
 * and merge in whatever it needs to add.
 */
function RedirectPreservingSearch({ to, merge }: { to: string; merge?: Record<string, string> }) {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  for (const [key, value] of Object.entries(merge ?? {})) params.set(key, value);
  const qs = params.toString();
  return <Navigate to={qs ? `${to}?${qs}` : to} replace />;
}

export function AppRoutes() {
  // index.html starts an HDR route black (`html.hdr-boot`) and without the Vault's Google Fonts (HdrcInter is
  // self-hosted; final review m6). Any location that is not an HDR route gets both back: the in-app hand-off
  // to the Vault, and AuthGuard's redirect from a signed-out /hdr, which never mounts HdrStudioPage — that
  // redirect used to leave the login page on a black body with no Vault fonts (L7 final review, measured).
  const { pathname } = useLocation();
  useEffect(() => {
    if (HDR_ROUTE.test(pathname)) return;
    document.documentElement.classList.remove("hdr-boot");
    (window as { __vaultFonts?: () => void }).__vaultFonts?.();
  }, [pathname]);
  return (
    <Routes>
      {/* Public */}
      <Route path="/" element={<GuestGuard><LoginPage /></GuestGuard>} />
      <Route path="/explore" element={<ExplorePage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/embed/:token" element={<EmbedPage />} />
      <Route path="/showcase/:slug" element={<ShowcasePage />} />
      <Route path="/showcase/:slug/:modelId" element={<ShowcaseModelPage />} />
      {/* Public client lookbook (Collections share link) */}
      <Route path="/l/:token" element={<Suspense fallback={null}><ClientLookbookPage /></Suspense>} />

      {/* Authed, pre-workspace */}
      <Route path="/select-workspace" element={<AuthGuard><WorkspaceSelectPage /></AuthGuard>} />
      <Route path="/welcome" element={<AuthGuard requireWorkspace><WelcomeWizard /></AuthGuard>} />
      <Route path="/invite/:token" element={<AuthGuard><AcceptInvitePage /></AuthGuard>} />
      <Route path="/account-pending" element={<AuthGuard><AccountPendingPage /></AuthGuard>} />
      <Route path="/account-blocked" element={<AuthGuard><AccountBlockedPage /></AuthGuard>} />

      {/* Sidebar shell */}
      <Route element={<AuthGuard requireWorkspace><VaultLayout /></AuthGuard>}>
        <Route path="/home" element={<HomePage />} />
        <Route path="/vault" element={<DrivePage />} />
        {/* New Vault sections */}
        <Route path="/collections" element={<CollectionsPage />} />
        <Route path="/collections/:id" element={<CollectionDetailPage />} />
        <Route path="/approvals" element={<ApprovalsPage />} />
        <Route path="/insights" element={<InsightsPage />} />
        <Route path="/links" element={<LinksPage />} />
        <Route path="/bulk" element={<BulkRenderPage />} />
        <Route path="/render-settings" element={<RenderSettingsPage />} />
        <Route path="/branding" element={<BrandingPage />} />
        <Route path="/storage" element={<StoragePage />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/billing" element={<BillingOverview />} />
        <Route path="/billing/plans" element={<ChoosePlanPage />} />
        <Route path="/settings" element={<SettingsPage />} />
      </Route>

      {/* Super-admin console */}
      <Route element={<AuthGuard requireSaasOwner><AdminLayout /></AuthGuard>}>
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/users/:id" element={<AdminUserDetail />} />
        <Route path="/admin/workspaces" element={<AdminWorkspaces />} />
        <Route path="/admin/plans" element={<AdminPlans />} />
        <Route path="/admin/presets" element={<AdminPresets />} />
        <Route path="/admin/billing" element={<AdminBilling />} />
        <Route path="/admin/invoices" element={<AdminInvoices />} />
        <Route path="/admin/broadcasts" element={<AdminBroadcasts />} />
        <Route path="/admin/support" element={<AdminSupport />} />
        <Route path="/admin/audit" element={<AdminAudit />} />
      </Route>

      {/* Full-screen viewer experiences */}
      {/* Dev: open a viewer-local file in the editor without Vault/auth —
          ?model=/models/x.glb (+ optional &vjson= / &config=) is forwarded
          into the viewer iframe by ViewerHost. No modelId → no Vault calls. */}
      <Route path="/dev/studio" element={<ErrorBoundary label="Studio"><StudioPage /></ErrorBoundary>} />
      {/* Dev: same unguarded passthrough as /dev/studio, so headless harnesses
          (playwright, dom-driver) can reach the Ring Builder without a Vault
          session. No modelId / no Vault calls either way. */}
      <Route path="/dev/ring" element={<ErrorBoundary label="Ring Builder"><RingBuilderPage /></ErrorBoundary>} />
      <Route path="/dev/engagement" element={<RedirectPreservingSearch to="/dev/ring" merge={{ engagement: "1" }} />} />
      {/* HDR Studio: an HDR lighting-map editor. Unguarded like /dev/studio, but a
          real production route (NOT the import.meta.env.DEV pattern below). */}
      <Route path="/dev/hdr" element={<ErrorBoundary label="HDR Studio"><HdrStudioPage /></ErrorBoundary>} />
      {DevConfiguratorPage ? (
        <Route path="/dev/configurator" element={<Suspense fallback={null}><DevConfiguratorPage /></Suspense>} />
      ) : null}
      <Route path="/view/:modelId" element={<AuthGuard requireWorkspace><ModelViewPage /></AuthGuard>} />
      {/* Canvas entry — empty studio to load a model into (sidebar "Canvas"). */}
      <Route path="/studio" element={<AuthGuard requireWorkspace><ErrorBoundary label="Studio"><StudioPage /></ErrorBoundary></AuthGuard>} />
      <Route path="/studio/:modelId" element={<AuthGuard requireWorkspace><ErrorBoundary label="Studio"><StudioPage /></ErrorBoundary></AuthGuard>} />
      {/* One merged builder: the wedding band is the base product, the
          Engagement Ring toggle attaches the head. */}
      <Route path="/ring" element={<AuthGuard requireWorkspace><ErrorBoundary label="Ring Builder"><RingBuilderPage /></ErrorBoundary></AuthGuard>} />
      <Route path="/hdr" element={<AuthGuard requireWorkspace><ErrorBoundary label="HDR Studio"><HdrStudioPage /></ErrorBoundary></AuthGuard>} />
      <Route path="/band" element={<RedirectPreservingSearch to="/ring" />} />
      <Route path="/engagement" element={<RedirectPreservingSearch to="/ring" merge={{ engagement: "1" }} />} />

      <Route path="*" element={<Navigate to="/vault" replace />} />
    </Routes>
  );
}
